<template>
  <div class="shortcut-tile-card flex flex-col justify-between">
    <!-- Top accent bar -->
    <div class="shortcut-accent-bar" :style="{ background: hex_color }"></div>

    <div class="p-4 flex flex-col justify-between flex-1">
      <!-- Main Content Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center min-w-0 pr-2">
          <!-- Icon Container -->
          <div
            class="shortcut-icon-wrapper flex items-center justify-center rounded-xl mr-3 flex-shrink-0"
            :style="{ background: rgb_color, color: hex_color, border: `1px solid ${rgb_color_border}` }"
          >
            <feather-icon
              :icon="icon || 'BarChart2Icon'"
              svgClasses="h-5 w-5"
              :style="{ color: hex_color }"
            />
          </div>

          <!-- Text Info -->
          <div class="min-w-0">
            <h4 class="shortcut-title font-bold text-base mb-0.5 leading-tight truncate" :title="statistic">
              {{ statistic }}
            </h4>
            <p class="shortcut-desc text-xs truncate">
              Overview of leads, accounts & actions
            </p>
          </div>
        </div>

        <!-- Arrow Action Hint -->
        <div class="shortcut-action-hint flex-shrink-0 ml-1">
          <feather-icon icon="ChevronRightIcon" svgClasses="h-4 w-4" />
        </div>
      </div>

      <!-- CRM Metrics Section -->
      <div class="shortcut-stats-row">
        <div class="shortcut-stat-pill">
          <span class="stat-pill-label">New</span>
          <vx-tooltip :text="`New Leads: ${formatFullNumber(crm_summary.new_leads)}`" position="top" class="stat-pill-tooltip">
            <span class="stat-pill-value text-primary">{{ formatCrmNumber(crm_summary.new_leads) }}</span>
          </vx-tooltip>
        </div>
        <div class="shortcut-stat-pill">
          <span class="stat-pill-label">Active</span>
          <vx-tooltip :text="`Active Leads: ${formatFullNumber(crm_summary.open_active_leads)}`" position="top" class="stat-pill-tooltip">
            <span class="stat-pill-value text-success">{{ formatCrmNumber(crm_summary.open_active_leads) }}</span>
          </vx-tooltip>
        </div>
        <div class="shortcut-stat-pill">
          <span class="stat-pill-label">Overdue</span>
          <vx-tooltip :text="`Overdue Follow-ups: ${formatFullNumber(crm_summary.follow_up_overdue)}`" position="top" class="stat-pill-tooltip">
            <span class="stat-pill-value text-danger">{{ formatCrmNumber(crm_summary.follow_up_overdue) }}</span>
          </vx-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const CrmRepository = RepositoryFactory.get('crm');
import _color from '@/assets/utils/color.js'

export default {
  name: 'CrmSummaryShortcutCard',
  props: {
    icon: {
      type: String,
      default: 'BarChart2Icon'
    },
    statistic: {
      type: [Number, String],
      default: 'CRM Summary'
    },
    statisticTitle: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: '#7367f0',
    },
    colorTo: {
      type: String
    },
    iconRight: {
      type: Boolean,
      default: false
    },
    hideChart: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      crm_summary: {
        new_leads: 0,
        open_active_leads: 0,
        follow_up_overdue: 0
      }
    }
  },
  methods: {
    formatCrmNumber(val) {
      if (val === undefined || val === null || val === '') return '0';
      const num = Number(val);
      if (isNaN(num)) return val;
      const abs = Math.abs(num);
      if (abs >= 1000000) {
        return (num / 1000000).toFixed(1).replace(/\.?0+$/, '') + 'M';
      }
      if (abs >= 10000) {
        return (num / 1000).toFixed(1).replace(/\.?0+$/, '') + 'k';
      }
      return num.toLocaleString();
    },
    formatFullNumber(val) {
      if (val === undefined || val === null || val === '') return '0';
      const num = Number(val);
      return isNaN(num) ? String(val) : num.toLocaleString();
    }
  },
  async mounted() {
    try {
      const { data } = (await CrmRepository.fetchCrmOwnerSummary()).data
      if (data) {
        this.crm_summary = data
      }
    } catch (error) {
    }
  },
  computed: {
    rgb_color: function () {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, .12)
      } else {
        return _color.getColor('#7367f0', .12)
      }
    },
    rgb_color_border: function () {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, .28)
      } else {
        return _color.getColor('#7367f0', .28)
      }
    },
    hex_color: function () {
      if (_color.isHexColor(this.color)) {
        return this.color
      } else {
        return '#7367f0'
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.shortcut-tile-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  height: 100%;
  min-height: 84px;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: rgba(var(--vs-primary), 0.35);

    .shortcut-action-hint {
      opacity: 1;
      transform: translateX(2px);
      color: rgba(var(--vs-primary), 1);
    }

    .shortcut-icon-wrapper {
      transform: scale(1.06);
    }
  }

  .shortcut-accent-bar {
    height: 3px;
    width: 100%;
    opacity: 0.9;
  }

  .shortcut-icon-wrapper {
    width: 40px;
    height: 40px;
    transition: transform 0.2s ease;
  }

  .shortcut-action-hint {
    opacity: 0.35;
    color: #82868b;
    transition: all 0.2s ease;
  }

  .shortcut-title {
    color: #2c2c2c;
  }

  .shortcut-desc {
    color: #727b87;
    line-height: 1.3;
  }

  .shortcut-stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-left: -3px;
    margin-right: -3px;
    gap: 4px;
  }

  .shortcut-stat-pill {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1 0%;
    min-width: 0;
    margin: 0;
    padding: 5px 6px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.04);
    overflow: hidden;

    .stat-pill-label {
      color: #7d8793;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      margin-right: 4px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .stat-pill-tooltip {
      min-width: 0;
      display: flex;
      justify-content: flex-end;
      overflow: hidden;
      margin-left: auto;
      max-width: 100%;
    }

    .stat-pill-value {
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: right;
      display: block;
      max-width: 100%;
    }
  }
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .shortcut-tile-card {
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
    background-color: #10163a;

    &:hover {
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
      border-color: rgba(115, 103, 240, 0.5);
    }

    .shortcut-title {
      color: #ebeefd;
    }

    .shortcut-desc {
      color: #9aa2ba;
    }

    .shortcut-action-hint {
      color: #9aa2ba;
      opacity: 0.45;
    }

    .shortcut-stats-row {
      border-top-color: rgba(255, 255, 255, 0.07);
    }

    .shortcut-stat-pill {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.06);

      .stat-pill-label {
        color: #a3adc2;
      }
    }
  }
}
</style>



